import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import api from "../../../api";
import { Loader } from "../loader";
import moment from "moment";
import { Pagination, Stack, Select, MenuItem } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import "./WaqarTradeMonitor.css";

const waqarTmSelectSx = {
  width: 280,
  maxWidth: "100%",
  background: "#ffffff",
  borderRadius: "14px",
  fontFamily: "'Space Grotesk', sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#10201b",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(16, 32, 27, 0.12)",
    borderRadius: "14px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(16, 32, 27, 0.22)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#14b8a6",
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    padding: "12px 16px",
  },
  "& .MuiSelect-icon": {
    color: "#0d9488",
  },
};

const waqarTmMenuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "14px",
      border: "1px solid rgba(16, 32, 27, 0.08)",
      boxShadow: "0 18px 44px rgba(16, 32, 27, 0.14)",
      "& .MuiMenuItem-root": {
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: 14,
        padding: "10px 16px",
      },
      "& .MuiMenuItem-root:hover": {
        background: "rgba(20, 184, 166, 0.08)",
      },
      "& .MuiMenuItem-root.Mui-selected": {
        background: "rgba(20, 184, 166, 0.12)",
        fontWeight: 600,
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        background: "rgba(20, 184, 166, 0.18)",
      },
    },
  },
};

const fmt = (n, d = 2) => (n == null ? "—" : Number(n).toFixed(d));
const fmtPrice = (n) => (n == null ? "—" : `$${Number(n).toFixed(4)}`);

const NICKNAMES = { ben: "Ibad", david: "Shehenshah", max: "Haider" };
const getDisplayName = (s) => {
  const name = s.name || s.email || "";
  const lower = name.toLowerCase();
  for (const [key, nick] of Object.entries(NICKNAMES)) {
    if (lower.includes(key)) return `${nick} - ${name} `;
  }
  return name;
};

const cardStyle = (bg) => ({
  background: bg,
  borderRadius: 16,
  padding: "18px 20px",
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  flex: "1 1 calc(25% - 11px)",
  minWidth: 160,
  maxWidth: "calc(25% - 11px)",
  boxSizing: "border-box",
});

const label = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1,
  textTransform: "uppercase",
  marginBottom: 6,
  opacity: 0.75,
};
const bigVal = (color) => ({
  fontSize: 20,
  fontWeight: 700,
  color,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const TH = ({ children, right }) => (
  <th
    style={{
      background: "#5e17eb",
      color: "#fff",
      padding: "10px 14px",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.8,
      textTransform: "uppercase",
      textAlign: right ? "right" : "left",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);
const TD = ({ children, right, style }) => (
  <td
    style={{
      padding: "9px 14px",
      fontSize: 13,
      textAlign: right ? "right" : "left",
      borderBottom: "1px solid #eee",
      ...style,
    }}
  >
    {children}
  </td>
);

const PositionBadge = ({ type }) => {
  const bg =
    type === "Long" ? "#d4f7e0" : type === "Short" ? "#fde8e8" : "#e8f0ff";
  const color =
    type === "Long" ? "#1a7a40" : type === "Short" ? "#c0392b" : "#3a5fc8";
  return (
    <span
      style={{
        background: bg,
        color,
        borderRadius: 6,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {type?.toUpperCase() || "—"}
    </span>
  );
};

const OutcomeBadge = ({ outcome }) => {
  const map = {
    "TP Hit": ["#d4f7e0", "#1a7a40"],
    "SL Hit": ["#fde8e8", "#c0392b"],
    Closed: ["#fff3cd", "#856404"],
  };
  const [bg, color] = map[outcome] || ["#e9ecef", "#555"];
  return (
    <span
      style={{
        background: bg,
        color,
        borderRadius: 6,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {outcome || "—"}
    </span>
  );
};

// ── Waqar-portal Trade Monitor presentation helpers (scoped to
//    <TradeMonitor/> below — TradeMonitorSelf keeps using the shared
//    TH/TD/PositionBadge/OutcomeBadge helpers above, untouched) ──
const waqarSourcePillMeta = (sourceType) => {
  switch (sourceType) {
    case "liveSession":
      return { cls: "waqar-tm-pill--live", text: "Live" };
    case "stock":
      return { cls: "waqar-tm-pill--stock", text: "Stock" };
    case "forex":
      return { cls: "waqar-tm-pill--forex", text: "Forex" };
    default:
      return { cls: "waqar-tm-pill--crypto", text: "Crypto" };
  }
};

const WaqarSourcePill = ({ sourceType }) => {
  const { cls, text } = waqarSourcePillMeta(sourceType);
  return <span className={`waqar-tm-pill ${cls}`}>{text}</span>;
};

const WaqarPositionPill = ({ type }) => {
  const t = (type || "").toLowerCase();
  const cls =
    t === "long"
      ? "waqar-tm-pill--long"
      : t === "short"
        ? "waqar-tm-pill--short"
        : t === "spot"
          ? "waqar-tm-pill--spot"
          : "waqar-tm-pill--neutral";
  return (
    <span className={`waqar-tm-pill ${cls}`}>{type?.toUpperCase() || "—"}</span>
  );
};

const WaqarOutcomePill = ({ outcome }) => {
  const cls =
    outcome === "TP Hit"
      ? "waqar-tm-pill--tp"
      : outcome === "SL Hit"
        ? "waqar-tm-pill--sl"
        : outcome === "Closed"
          ? "waqar-tm-pill--closed"
          : "waqar-tm-pill--neutral";
  return <span className={`waqar-tm-pill ${cls}`}>{outcome || "—"}</span>;
};

export const TradeMonitor = () => {
  const [loader, setLoader] = useState(false);
  const [supportList, setSupportList] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [portfolio, setPortfolio] = useState(null);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [totalUnrealized, setTotalUnrealized] = useState(0);
  const [closedPage, setClosedPage] = useState(1);
  const [closedTotalPages, setClosedTotalPages] = useState(1);
  const [deletingId, setDeletingId] = useState(null);
  const [closingId, setClosingId] = useState(null);

  useEffect(() => {
    api
      .getSupportAdmin(false)
      .then((res) => {
        if (res?.success) setSupportList(res.support || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadAll(selectedId);

    const interval = setInterval(() => loadAll(selectedId), 30000);
    return () => clearInterval(interval);
  }, [selectedId]);

  const loadAll = async (adminId, page = 1) => {
    setLoader(true);
    setPortfolio(null);
    setOpenTrades([]);
    setClosedTrades([]);
    setTotalUnrealized(0);
    setClosedPage(1);
    setClosedTotalPages(1);
    try {
      const [portRes, liveRes, closedRes] = await Promise.all([
        api.getSupportPortfolio(adminId),
        api.getSupportLiveTrades(adminId),
        api.getSupportTrades(adminId, "Closed", 1),
      ]);
      if (portRes?.success) setPortfolio(portRes.portfolio);
      if (liveRes?.success) {
        setOpenTrades(liveRes.trades || []);
        setTotalUnrealized(liveRes.totalUnrealizedPnl || 0);
      }
      if (closedRes?.success) {
        setClosedTrades(closedRes.trades || []);
        setClosedTotalPages(closedRes.totalPages || 1);
      }
    } catch (e) {
      console.error(e);
    }
    setLoader(false);
  };

  const loadClosedPage = async (adminId, page) => {
    try {
      const res = await api.getSupportTrades(adminId, "Closed", page);
      if (res?.success) {
        setClosedTrades(res.trades || []);
        setClosedTotalPages(res.totalPages || 1);
        setClosedPage(page);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCloseTrade = async (entryId, currentPrice, symbol) => {
    if (
      !window.confirm(
        `Close ${symbol} at $${currentPrice}?\n\nP&L will be calculated at the current price and portfolio updated.`,
      )
    )
      return;
    try {
      setClosingId(entryId);
      const res = await api.closeSupportTradeAtMarket(entryId, currentPrice);
      if (res?.success) loadAll(selectedId, closedPage);
    } catch (e) {
      console.error("[closeTrade]", e);
      alert("Failed to close trade. Please try again.");
    } finally {
      setClosingId(null);
    }
  };

  const handleDeleteTrade = async (entryId, symbol, status) => {
    const label = status === "Open" ? "open" : "closed";
    if (
      !window.confirm(
        `Delete this ${label} trade (${symbol})?\n\nBalance used will be fully restored to the member's portfolio.`,
      )
    )
      return;
    try {
      setDeletingId(entryId);
      const res = await api.deleteSupportTradeEntry(entryId);
      if (res?.success) loadAll(selectedId, closedPage);
    } catch (e) {
      console.error("[deleteTrade]", e);
      alert("Failed to delete trade. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // Derived stats
  const winRate = (() => {
    if (!portfolio) return 0;
    const settled = portfolio.winTrades + portfolio.lossTrades;
    return settled === 0
      ? 0
      : ((portfolio.winTrades / settled) * 100).toFixed(1);
  })();

  const breakEvenRate = (() => {
    if (!portfolio) return 0;
    const avgWin = portfolio.avgWinUSDT || 0;
    const avgLoss = Math.abs(portfolio.avgLossUSDT || 0);
    const denom = avgWin + avgLoss;
    return denom === 0 ? 0 : ((avgLoss / denom) * 100).toFixed(1);
  })();

  const belowBreakEven = portfolio
    ? (parseFloat(breakEvenRate) - parseFloat(winRate)).toFixed(1)
    : 0;

  return (
    <div className="waqar-tm">
      {/* Selector */}
      <div className="waqar-tm-selector-row">
        <Select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          displayEmpty
          IconComponent={KeyboardArrowDownRoundedIcon}
          MenuProps={waqarTmMenuProps}
          sx={waqarTmSelectSx}
        >
          <MenuItem value="">
            <span style={{ color: "#8d9b96" }}>
              — Select Support Member —
            </span>
          </MenuItem>
          {supportList.map((s) => (
            <MenuItem key={s._id} value={s._id}>
              {getDisplayName(s)}
            </MenuItem>
          ))}
        </Select>
        {selectedId && (
          <span className="waqar-tm-live-note">
            <span className="waqar-tm-live-dot" />
            auto-refreshes every 30s
          </span>
        )}
      </div>

      {loader && <Loader />}

      {!selectedId && !loader && (
        <div className="waqar-tm-empty">
          Select a support member to view their trade performance.
        </div>
      )}

      {selectedId && !loader && (
        <>
          {/* ── Stat Cards ── */}
          <div className="waqar-tm-stats-grid">
            <div className="waqar-tm-stat-card waqar-tm-stat--balance">
              <div className="waqar-tm-stat-label">Balance</div>
              <div className="waqar-tm-stat-value">
                ${fmt(portfolio?.currentBalance ?? 0)}
              </div>
            </div>

            <div
              className={`waqar-tm-stat-card ${
                totalUnrealized >= 0
                  ? "waqar-tm-stat--unrealized-up"
                  : "waqar-tm-stat--unrealized-down"
              }`}
            >
              <div className="waqar-tm-stat-label">Unrealized P&L</div>
              <div className="waqar-tm-stat-value">
                {totalUnrealized >= 0 ? "+" : ""}${fmt(totalUnrealized)}
              </div>
            </div>

            <div
              className={`waqar-tm-stat-card ${
                (portfolio?.totalPnlUSDT ?? 0) >= 0
                  ? "waqar-tm-stat--realized-up"
                  : "waqar-tm-stat--realized-down"
              }`}
            >
              <div className="waqar-tm-stat-label">Realized P&L</div>
              <div className="waqar-tm-stat-value">
                {(portfolio?.totalPnlUSDT ?? 0) >= 0 ? "+" : ""}$
                {fmt(portfolio?.totalPnlUSDT ?? 0)}
              </div>
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--open">
              <div className="waqar-tm-stat-label">Open Trades</div>
              <div className="waqar-tm-stat-value">{openTrades.length}</div>
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--winrate">
              <div className="waqar-tm-stat-label">Win Rate</div>
              <div className="waqar-tm-stat-value">{winRate}%</div>
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--wlt">
              <div className="waqar-tm-stat-label">
                Wins / Losses / Timeouts
              </div>
              <div className="waqar-tm-wlt">
                <span className="w">{portfolio?.winTrades ?? 0}W</span>
                <span className="sep">/</span>
                <span className="l">{portfolio?.lossTrades ?? 0}L</span>
                <span className="sep">/</span>
                <span className="t">{portfolio?.timeoutTrades ?? 0}T</span>
              </div>
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--breakeven">
              <div className="waqar-tm-stat-label">Break-even Win Rate</div>
              <div className="waqar-tm-stat-value">{breakEvenRate}%</div>
              <div className="waqar-tm-stat-sub">
                avg win ${fmt(portfolio?.avgWinUSDT ?? 0)} · avg loss $
                {fmt(Math.abs(portfolio?.avgLossUSDT ?? 0))}
              </div>
              {belowBreakEven > 0 && (
                <div className="waqar-tm-stat-sub">
                  you are <strong>{belowBreakEven} pts</strong> below it
                </div>
              )}
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--starting">
              <div className="waqar-tm-stat-label">Starting Balance</div>
              <div className="waqar-tm-stat-value">
                ${fmt(portfolio?.allocatedBalance ?? 0)}
              </div>
            </div>
          </div>

          {/* ── Open Trades ── */}
          <div className="waqar-tm-section">
            <div className="waqar-tm-table-card">
              <div className="waqar-tm-section-header">
                <span className="waqar-tm-section-title">Open Trades</span>
                <span className="waqar-tm-count-badge">
                  {openTrades.length}
                </span>
              </div>
              <div className="waqar-tm-table-scroll">
                <table className="waqar-tm-table">
                  <thead>
                    <tr className="waqar-tm-thead-row">
                      <th className="waqar-tm-th">Symbol</th>
                      <th className="waqar-tm-th">Source</th>
                      <th className="waqar-tm-th">Position</th>
                      <th className="waqar-tm-th">Entry</th>
                      <th className="waqar-tm-th">Target (TP)</th>
                      <th className="waqar-tm-th">Stop (SL)</th>
                      <th className="waqar-tm-th">Current Price</th>
                      <th className="waqar-tm-th waqar-tm-th--right">
                        Unrealized P&L
                      </th>
                      <th className="waqar-tm-th waqar-tm-th--right">Size</th>
                      <th className="waqar-tm-th">Opened</th>
                      <th className="waqar-tm-th"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {openTrades.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="waqar-tm-empty-row">
                          No open trades
                        </td>
                      </tr>
                    ) : (
                      openTrades.map((t) => (
                        <tr key={t._id} className="waqar-tm-row">
                          <td className="waqar-tm-td">
                            <span className="waqar-tm-symbol">
                              {t.coin || t.currency || "—"}
                            </span>
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarSourcePill sourceType={t.sourceType} />
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarPositionPill
                              type={
                                t.positionType ||
                                (t.tradeType === "spot" ? "Spot" : null)
                              }
                            />
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono">
                            {fmtPrice(t.entryPrice)}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-tp">
                            {fmtPrice(t.takeProfit)}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-sl">
                            {t.stopLoss ? fmtPrice(t.stopLoss) : "—"}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono">
                            {t.currentPrice ? (
                              fmtPrice(t.currentPrice)
                            ) : (
                              <span className="waqar-tm-muted">Loading…</span>
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-td--right">
                            {t.unrealizedPnl != null ? (
                              <>
                                <span
                                  className={
                                    t.unrealizedPnl >= 0
                                      ? "waqar-tm-pnl-up"
                                      : "waqar-tm-pnl-down"
                                  }
                                >
                                  {t.unrealizedPnl >= 0 ? "+" : ""}
                                  {fmt(t.unrealizedPnl)} USDT (
                                  {fmt(t.unrealizedRoi)}%)
                                </span>
                                {t.unrealizedRoi >= 5 && t.currentPrice && (
                                  <div>
                                    <button
                                      className="waqar-tm-close-btn"
                                      onClick={() =>
                                        handleCloseTrade(
                                          t._id,
                                          t.currentPrice,
                                          t.coin || t.currency || "?",
                                        )
                                      }
                                      disabled={closingId === t._id}
                                    >
                                      {closingId === t._id
                                        ? "Closing…"
                                        : "Close Trade"}
                                    </button>
                                  </div>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-td--right waqar-tm-mono">
                            ${fmt(t.balanceUsed)}
                            {t.tradeType === "futures" && t.leverage > 1 && (
                              <span className="waqar-tm-leverage">
                                {t.leverage}x
                              </span>
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-muted">
                            {moment(t.createdAt).format("M/D/YYYY, h:mm:ss A")}
                          </td>
                          <td className="waqar-tm-td">
                            <button
                              className="waqar-tm-del-btn"
                              onClick={() =>
                                handleDeleteTrade(
                                  t._id,
                                  t.coin || t.currency || "?",
                                  "Open",
                                )
                              }
                              disabled={deletingId === t._id}
                            >
                              {deletingId === t._id ? "…" : "✕"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Closed Trades ── */}
          <div className="waqar-tm-section">
            <div className="waqar-tm-table-card">
              <div className="waqar-tm-section-header">
                <span className="waqar-tm-section-title">Closed Trades</span>
                <span className="waqar-tm-count-badge">
                  {closedTrades.length}
                </span>
              </div>
              <div className="waqar-tm-table-scroll">
                <table className="waqar-tm-table">
                  <thead>
                    <tr className="waqar-tm-thead-row">
                      <th className="waqar-tm-th">Symbol</th>
                      <th className="waqar-tm-th">Source</th>
                      <th className="waqar-tm-th">Position</th>
                      <th className="waqar-tm-th">Entry</th>
                      <th className="waqar-tm-th">Exit</th>
                      <th className="waqar-tm-th">Outcome</th>
                      <th className="waqar-tm-th waqar-tm-th--right">P&L</th>
                      <th className="waqar-tm-th waqar-tm-th--right">Size</th>
                      <th className="waqar-tm-th">Opened</th>
                      <th className="waqar-tm-th">Closed</th>
                      <th className="waqar-tm-th"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedTrades.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="waqar-tm-empty-row">
                          No closed trades
                        </td>
                      </tr>
                    ) : (
                      closedTrades.map((t) => (
                        <tr key={t._id} className="waqar-tm-row">
                          <td className="waqar-tm-td">
                            <span className="waqar-tm-symbol">
                              {t.coin || t.currency || "—"}
                            </span>
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarSourcePill sourceType={t.sourceType} />
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarPositionPill
                              type={
                                t.positionType ||
                                (t.tradeType === "spot" ? "Spot" : null)
                              }
                            />
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono">
                            {fmtPrice(t.entryPrice)}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono">
                            {t.closedPrice ? fmtPrice(t.closedPrice) : "—"}
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarOutcomePill outcome={t.outcome} />
                          </td>
                          <td className="waqar-tm-td waqar-tm-td--right">
                            {t.pnlUSDT != null ? (
                              <span
                                className={
                                  t.pnlUSDT >= 0
                                    ? "waqar-tm-pnl-up"
                                    : "waqar-tm-pnl-down"
                                }
                              >
                                {t.pnlUSDT >= 0 ? "+" : ""}
                                {fmt(t.pnlUSDT)} USDT ({fmt(t.roiPercent)}%)
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-td--right waqar-tm-mono">
                            ${fmt(t.balanceUsed)}
                            {t.tradeType === "futures" && t.leverage > 1 && (
                              <span className="waqar-tm-leverage">
                                {t.leverage}x
                              </span>
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-muted">
                            {moment(t.createdAt).format("M/D/YYYY, h:mm:ss A")}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-muted">
                            {t.resolvedAt
                              ? moment(t.resolvedAt).format(
                                  "M/D/YYYY, h:mm:ss A",
                                )
                              : "—"}
                          </td>
                          <td className="waqar-tm-td">
                            <button
                              className="waqar-tm-del-btn"
                              onClick={() =>
                                handleDeleteTrade(
                                  t._id,
                                  t.coin || t.currency || "?",
                                  "Closed",
                                )
                              }
                              disabled={deletingId === t._id}
                            >
                              {deletingId === t._id ? "…" : "✕"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {closedTotalPages > 1 && (
                <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                  <Pagination
                    count={closedTotalPages}
                    page={closedPage}
                    onChange={(_, v) => loadClosedPage(selectedId, v)}
                    color="primary"
                    size="small"
                  />
                </Stack>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── Support panel version — auto-loads with the logged-in member's own ID ──
export const TradeMonitorSelf = () => {
  const adminStore = useSelector((state) => state.adminReducer);
  const adminId = adminStore?.admin?.admin?._id;

  const [loader, setLoader] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [totalUnrealized, setTotalUnrealized] = useState(0);
  const [closedPage, setClosedPage] = useState(1);
  const [closedTotalPages, setClosedTotalPages] = useState(1);
  const [closingId, setClosingId] = useState(null);

  useEffect(() => {
    if (!adminId) return;
    loadAll(adminId);
    const interval = setInterval(() => loadAll(adminId), 30000);
    return () => clearInterval(interval);
  }, [adminId]);

  const loadAll = async (id) => {
    setLoader(true);
    setPortfolio(null);
    setOpenTrades([]);
    setClosedTrades([]);
    setTotalUnrealized(0);
    setClosedPage(1);
    setClosedTotalPages(1);
    try {
      const [portRes, liveRes, closedRes] = await Promise.all([
        api.getSupportPortfolio(id),
        api.getSupportLiveTrades(id),
        api.getSupportTrades(id, "Closed", 1),
      ]);
      if (portRes?.success) setPortfolio(portRes.portfolio);
      if (liveRes?.success) {
        setOpenTrades(liveRes.trades || []);
        setTotalUnrealized(liveRes.totalUnrealizedPnl || 0);
      }
      if (closedRes?.success) {
        setClosedTrades(closedRes.trades || []);
        setClosedTotalPages(closedRes.totalPages || 1);
      }
    } catch (e) {
      console.error(e);
    }
    setLoader(false);
  };

  const loadClosedPage = async (page) => {
    try {
      const res = await api.getSupportTrades(adminId, "Closed", page);
      if (res?.success) {
        setClosedTrades(res.trades || []);
        setClosedTotalPages(res.totalPages || 1);
        setClosedPage(page);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCloseTrade = async (entryId, currentPrice, symbol) => {
    if (
      !window.confirm(
        `Close trade for ${symbol} at current price $${currentPrice}?`,
      )
    )
      return;
    setClosingId(entryId);
    try {
      const res = await api.closeSupportTradeAtMarket(entryId, currentPrice);
      if (res?.success) loadAll(adminId);
      else alert(res?.message || "Failed to close trade");
    } catch (e) {
      alert("Error closing trade");
    }
    setClosingId(null);
  };

  const winRate = (() => {
    if (!portfolio) return 0;
    const settled = portfolio.winTrades + portfolio.lossTrades;
    return settled === 0
      ? 0
      : ((portfolio.winTrades / settled) * 100).toFixed(1);
  })();

  const breakEvenRate = (() => {
    if (!portfolio) return 0;
    const avgWin = portfolio.avgWinUSDT || 0;
    const avgLoss = Math.abs(portfolio.avgLossUSDT || 0);
    const denom = avgWin + avgLoss;
    return denom === 0 ? 0 : ((avgLoss / denom) * 100).toFixed(1);
  })();

  const belowBreakEven = portfolio
    ? (parseFloat(breakEvenRate) - parseFloat(winRate)).toFixed(1)
    : 0;

  if (!adminId) return null;

  return (
    <div style={{ padding: "0 15px" }}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 16 }}>
          My Trade Performance
        </span>
        <span style={{ fontSize: 11, color: "#aaa" }}>
          auto-refreshes every 30s
        </span>
      </div>

      {loader && <Loader />}

      {!loader && (
        <>
          {/* ── Stat Cards ── */}
          <div
            style={{
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
              marginBottom: 28,
            }}
          >
            <div style={cardStyle("#f5f0ff")}>
              <div style={label}>Balance</div>
              <div style={bigVal("#3d0f8a")}>
                ${fmt(portfolio?.currentBalance ?? 0)}
              </div>
            </div>
            <div style={cardStyle("#fff0f0")}>
              <div style={label}>Unrealized P&L</div>
              <div style={bigVal(totalUnrealized >= 0 ? "#1a7a40" : "#c0392b")}>
                {totalUnrealized >= 0 ? "+" : ""}${fmt(totalUnrealized)}
              </div>
            </div>
            <div style={cardStyle("#fff0f0")}>
              <div style={label}>Realized P&L</div>
              <div
                style={bigVal(
                  (portfolio?.totalPnlUSDT ?? 0) >= 0 ? "#1a7a40" : "#c0392b",
                )}
              >
                {(portfolio?.totalPnlUSDT ?? 0) >= 0 ? "+" : ""}$
                {fmt(portfolio?.totalPnlUSDT ?? 0)}
              </div>
            </div>
            <div style={cardStyle("#fffde7")}>
              <div style={label}>Open Trades</div>
              <div style={bigVal("#e6a817")}>{openTrades.length}</div>
            </div>
            <div style={cardStyle("#f0fff4")}>
              <div style={label}>Win Rate</div>
              <div style={bigVal("#1a7a40")}>{winRate}%</div>
            </div>
            <div style={cardStyle("#f5f0ff")}>
              <div style={label}>Wins / Losses / Timeouts</div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ color: "#1a7a40" }}>
                  {portfolio?.winTrades ?? 0}W
                </span>
                <span style={{ color: "#aaa", margin: "0 4px" }}>/</span>
                <span style={{ color: "#c0392b" }}>
                  {portfolio?.lossTrades ?? 0}L
                </span>
                <span style={{ color: "#aaa", margin: "0 4px" }}>/</span>
                <span style={{ color: "#e6a817" }}>
                  {portfolio?.timeoutTrades ?? 0}T
                </span>
              </div>
            </div>
            <div style={cardStyle("#fff0f0")}>
              <div style={label}>Break-even Win Rate</div>
              <div style={bigVal("#c0392b")}>{breakEvenRate}%</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                avg win ${fmt(portfolio?.avgWinUSDT ?? 0)} · avg loss $
                {fmt(Math.abs(portfolio?.avgLossUSDT ?? 0))}
              </div>
              {belowBreakEven > 0 && (
                <div style={{ fontSize: 11, color: "#c0392b", marginTop: 2 }}>
                  you are <strong>{belowBreakEven} pts</strong> below it
                </div>
              )}
            </div>
            <div style={cardStyle("#f8f8f8")}>
              <div style={label}>Starting Balance</div>
              <div style={bigVal("#222")}>
                ${fmt(portfolio?.allocatedBalance ?? 0)}
              </div>
            </div>
          </div>

          {/* ── Open Trades ── */}
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <span
                style={{ fontWeight: 700, fontSize: 14, letterSpacing: 0.5 }}
              >
                OPEN TRADES
              </span>
              <span
                style={{
                  background: "#5e17eb",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "2px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {openTrades.length}
              </span>
            </div>
            <div
              style={{
                overflowX: "auto",
                borderRadius: 10,
                boxShadow: "0 1px 8px rgba(0,0,0,0.08)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  background: "#fff",
                }}
              >
                <thead>
                  <tr>
                    <TH>Symbol</TH>
                    <TH>Source</TH>
                    <TH>Position</TH>
                    <TH>Entry</TH>
                    <TH>Target (TP)</TH>
                    <TH>Stop (SL)</TH>
                    <TH>Current Price</TH>
                    <TH right>Unrealized P&L</TH>
                    <TH right>Size</TH>
                    <TH>Opened</TH>
                  </tr>
                </thead>
                <tbody>
                  {openTrades.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        style={{
                          textAlign: "center",
                          padding: 20,
                          color: "#aaa",
                        }}
                      >
                        No open trades
                      </td>
                    </tr>
                  ) : (
                    openTrades.map((t) => (
                      <tr key={t._id} style={{ background: "#fff" }}>
                        <TD>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>
                            {t.coin || t.currency || "—"}
                          </span>
                        </TD>
                        <TD>
                          <span
                            style={{
                              background:
                                t.sourceType === "liveSession"
                                  ? "#e8f4fd"
                                  : t.sourceType === "stock"
                                    ? "#e8f5e9"
                                    : t.sourceType === "forex"
                                      ? "#fff3e0"
                                      : "#f0f0ff",
                              color:
                                t.sourceType === "liveSession"
                                  ? "#1565c0"
                                  : t.sourceType === "stock"
                                    ? "#2e7d32"
                                    : t.sourceType === "forex"
                                      ? "#e65100"
                                      : "#5e17eb",
                              borderRadius: 6,
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {t.sourceType === "liveSession"
                              ? "Live"
                              : t.sourceType === "stock"
                                ? "Stock"
                                : t.sourceType === "forex"
                                  ? "Forex"
                                  : "Crypto"}
                          </span>
                        </TD>
                        <TD>
                          <PositionBadge
                            type={
                              t.positionType ||
                              (t.tradeType === "spot" ? "Spot" : null)
                            }
                          />
                        </TD>
                        <TD>{fmtPrice(t.entryPrice)}</TD>
                        <TD style={{ color: "#1a7a40" }}>
                          {fmtPrice(t.takeProfit)}
                        </TD>
                        <TD style={{ color: "#c0392b" }}>
                          {t.stopLoss ? fmtPrice(t.stopLoss) : "—"}
                        </TD>
                        <TD>
                          {t.currentPrice ? (
                            fmtPrice(t.currentPrice)
                          ) : (
                            <span style={{ color: "#aaa" }}>Loading…</span>
                          )}
                        </TD>
                        <TD right>
                          {t.unrealizedPnl != null ? (
                            <span
                              style={{
                                color:
                                  t.unrealizedPnl >= 0 ? "#1a7a40" : "#c0392b",
                                fontWeight: 600,
                              }}
                            >
                              {t.unrealizedPnl >= 0 ? "+" : ""}
                              {fmt(t.unrealizedPnl)} USDT (
                              {fmt(t.unrealizedRoi)}%)
                            </span>
                          ) : (
                            "—"
                          )}
                          {t.unrealizedRoi >= 5 && t.currentPrice && (
                            <div style={{ marginTop: 5 }}>
                              <button
                                onClick={() =>
                                  handleCloseTrade(
                                    t._id,
                                    t.currentPrice,
                                    t.coin || t.currency || "?",
                                  )
                                }
                                disabled={closingId === t._id}
                                style={{
                                  background: "#16a34a",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 6,
                                  padding: "3px 10px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  opacity: closingId === t._id ? 0.6 : 1,
                                }}
                              >
                                {closingId === t._id
                                  ? "Closing…"
                                  : "Close Trade"}
                              </button>
                            </div>
                          )}
                        </TD>
                        <TD right>
                          <span>${fmt(t.balanceUsed)}</span>
                          {t.tradeType === "futures" && t.leverage > 1 && (
                            <span
                              style={{
                                marginLeft: 5,
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#5e17eb",
                                background: "#f0ebff",
                                borderRadius: 4,
                                padding: "1px 5px",
                              }}
                            >
                              {t.leverage}x
                            </span>
                          )}
                        </TD>
                        <TD>
                          {moment(t.createdAt).format("M/D/YYYY, h:mm:ss A")}
                        </TD>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Closed Trades ── */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <span
                style={{ fontWeight: 700, fontSize: 14, letterSpacing: 0.5 }}
              >
                CLOSED TRADES
              </span>
              <span
                style={{
                  background: "#5e17eb",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "2px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {closedTrades.length}
              </span>
            </div>
            <div
              style={{
                overflowX: "auto",
                borderRadius: 10,
                boxShadow: "0 1px 8px rgba(0,0,0,0.08)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  background: "#fff",
                }}
              >
                <thead>
                  <tr>
                    <TH>Symbol</TH>
                    <TH>Source</TH>
                    <TH>Position</TH>
                    <TH>Entry</TH>
                    <TH>Exit</TH>
                    <TH>Outcome</TH>
                    <TH right>P&L</TH>
                    <TH>Opened</TH>
                    <TH>Closed</TH>
                  </tr>
                </thead>
                <tbody>
                  {closedTrades.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          padding: 20,
                          color: "#aaa",
                        }}
                      >
                        No closed trades
                      </td>
                    </tr>
                  ) : (
                    closedTrades.map((t) => (
                      <tr key={t._id} style={{ background: "#fff" }}>
                        <TD>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>
                            {t.coin || t.currency || "—"}
                          </span>
                        </TD>
                        <TD>
                          <span
                            style={{
                              background:
                                t.sourceType === "liveSession"
                                  ? "#e8f4fd"
                                  : t.sourceType === "stock"
                                    ? "#e8f5e9"
                                    : t.sourceType === "forex"
                                      ? "#fff3e0"
                                      : "#f0f0ff",
                              color:
                                t.sourceType === "liveSession"
                                  ? "#1565c0"
                                  : t.sourceType === "stock"
                                    ? "#2e7d32"
                                    : t.sourceType === "forex"
                                      ? "#e65100"
                                      : "#5e17eb",
                              borderRadius: 6,
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {t.sourceType === "liveSession"
                              ? "Live"
                              : t.sourceType === "stock"
                                ? "Stock"
                                : t.sourceType === "forex"
                                  ? "Forex"
                                  : "Crypto"}
                          </span>
                        </TD>
                        <TD>
                          <PositionBadge
                            type={
                              t.positionType ||
                              (t.tradeType === "spot" ? "Spot" : null)
                            }
                          />
                        </TD>
                        <TD>{fmtPrice(t.entryPrice)}</TD>
                        <TD>{t.closedPrice ? fmtPrice(t.closedPrice) : "—"}</TD>
                        <TD>
                          <OutcomeBadge outcome={t.outcome} />
                        </TD>
                        <TD right>
                          {t.pnlUSDT != null ? (
                            <span
                              style={{
                                color: t.pnlUSDT >= 0 ? "#1a7a40" : "#c0392b",
                                fontWeight: 600,
                              }}
                            >
                              {t.pnlUSDT >= 0 ? "+" : ""}
                              {fmt(t.pnlUSDT)} USDT ({fmt(t.roiPercent)}%)
                            </span>
                          ) : (
                            "—"
                          )}
                        </TD>
                        <TD>
                          {moment(t.createdAt).format("M/D/YYYY, h:mm:ss A")}
                        </TD>
                        <TD>
                          {t.resolvedAt
                            ? moment(t.resolvedAt).format("M/D/YYYY, h:mm:ss A")
                            : "—"}
                        </TD>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {closedTotalPages > 1 && (
              <Stack spacing={2} alignItems="center" sx={{ mt: 2, mb: 1 }}>
                <Pagination
                  count={closedTotalPages}
                  page={closedPage}
                  onChange={(_, v) => loadClosedPage(v)}
                  color="primary"
                  size="small"
                />
              </Stack>
            )}
          </div>
        </>
      )}
    </div>
  );
};
